/**
 * Apunte Digital statistics/analytics screen.
 *
 * Numeric summary + theme-aware inline bar charts (no library):
 *  - Writing activity per day (last 2 weeks).
 *  - Most linked pages (link map).
 *  - Longest pages by number of blocks.
 * Everything derived locally; accessible (aria, sr-only table in the charts).
 */
import { msg } from "@lingui/core/macro";
import { type Component, type ComponentProps, createMemo, For, Show } from "solid-js";
import { Link, useNavigate } from "@tanstack/solid-router";
import { BarChart3, FileText, Hash, Link2, Type } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "@/components/ui/empty-state";
import { NoDataIllustration } from "@/components/ui/illustrations";
import { BarChart, type BarDatum } from "@/components/ui/bar-chart";
import { Button } from "@/components/ui/button";
import { useNotesStats } from "../use-notes-stats";
import { GamificationPanel } from "./gamification-panel";

function StatCard(props: { icon: Component<ComponentProps<"svg">>; label: string; value: string }) {
  return (
    <div class="rounded-xl border border-border/50 bg-card/40 p-4">
      <div class="flex items-center gap-2 text-muted-foreground/70">
        <props.icon class="h-4 w-4" />
        <span class="text-[11px] font-semibold uppercase tracking-wider">{props.label}</span>
      </div>
      <div class="mt-2 text-3xl font-bold tabular-nums">{props.value}</div>
    </div>
  );
}

export function StatsView() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const stats = useNotesStats();

  const numberFormat = createMemo(() => new Intl.NumberFormat(locale()));
  const fmt = (n: number) => numberFormat().format(n);

  const weekdayFormat = createMemo(() => new Intl.DateTimeFormat(locale(), { weekday: "short" }));
  const fullDateFormat = createMemo(
    () =>
      new Intl.DateTimeFormat(locale(), {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
  );

  const activityData = createMemo<BarDatum[]>(() =>
    stats.activity().map((d) => {
      // oxlint-disable-next-line lingui/no-unlocalized-strings -- ISO date literal, not UI copy
      const date = new Date(`${d.date}T00:00:00`);
      return {
        label: weekdayFormat().format(date).slice(0, 2),
        value: d.count,
        title: `${fullDateFormat().format(date)}: ${fmt(d.count)}`,
      };
    }),
  );

  const linkedData = createMemo<BarDatum[]>(() =>
    stats.topLinkedPages().map((entry) => ({
      label: entry.page.title?.trim() || t(msg`Untitled`),
      value: entry.count,
    })),
  );

  const longestData = createMemo<BarDatum[]>(() =>
    stats.longestPages().map((entry) => ({
      label: entry.page.title?.trim() || t(msg`Untitled`),
      value: entry.count,
    })),
  );

  return (
    <div
      class="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 [scrollbar-gutter:stable]"
      style={{ "overflow-anchor": "none" }}
    >
      <div class="mx-auto max-w-5xl space-y-10">
        <header class="space-y-2">
          <h1 class="flex items-center gap-3 font-serif text-4xl font-semibold tracking-tight md:text-5xl">
            <BarChart3 class="h-8 w-8 text-accent" />
            {t(msg`Statistics`)}
          </h1>
          <p class="max-w-xl text-sm text-muted-foreground">
            {t(msg`An overview of your writing activity, pages, words and link map.`)}
          </p>
        </header>

        <Show
          when={stats.hasData()}
          fallback={
            <EmptyState
              icon={BarChart3}
              illustration={<NoDataIllustration />}
              title={t(msg`No data yet`)}
              description={t(
                msg`Create pages and start writing — your statistics will appear here.`,
              )}
              onIconActivate={() => {
                void navigate({ to: "/notes" });
              }}
              iconActionLabel={t(msg`Go to notes`)}
              action={
                <Link to="/notes">
                  <Button class="gap-2">
                    <FileText class="h-4 w-4" />
                    {t(msg`Go to notes`)}
                  </Button>
                </Link>
              }
            />
          }
        >
          {/* Gamification (level/XP, activity streak, mission, badges) */}
          <GamificationPanel />

          {/* Numeric summary */}
          <section class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard icon={FileText} label={t(msg`Pages`)} value={fmt(stats.totalPages())} />
            <StatCard icon={Type} label={t(msg`Words`)} value={fmt(stats.totalWords())} />
            <StatCard icon={Hash} label={t(msg`Blocks`)} value={fmt(stats.totalBlocks())} />
            <StatCard icon={Link2} label={t(msg`Links`)} value={fmt(stats.totalLinks())} />
          </section>

          {/* Writing activity per day */}
          <section class="space-y-4 rounded-xl border border-border/40 bg-card/30 p-5 md:p-6">
            <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Writing activity (last 2 weeks)`)}
            </h2>
            <BarChart
              data={activityData()}
              orientation="vertical"
              ariaLabel={t(msg`Blocks edited per day over the last two weeks`)}
              barClass="bg-accent"
              formatValue={fmt}
            />
          </section>

          <div class="grid gap-6 lg:grid-cols-2">
            {/* Mapa de enlaces */}
            <section class="space-y-4 rounded-xl border border-border/40 bg-card/30 p-5 md:p-6">
              <h2 class="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-muted-foreground/70">
                <Link2 class="h-4 w-4" />
                {t(msg`Most linked pages`)}
              </h2>
              <Show
                when={linkedData().length > 0}
                fallback={
                  <p class="py-4 text-sm text-muted-foreground">
                    {t(msg`No wiki-links yet. Type [[ in a note to link pages.`)}
                  </p>
                }
              >
                <BarChart
                  data={linkedData()}
                  orientation="horizontal"
                  ariaLabel={t(msg`Pages ranked by incoming wiki-links`)}
                  barClass="bg-primary"
                  formatValue={fmt}
                />
              </Show>
            </section>

            {/* Longest pages */}
            <section class="space-y-4 rounded-xl border border-border/40 bg-card/30 p-5 md:p-6">
              <h2 class="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-muted-foreground/70">
                <Hash class="h-4 w-4" />
                {t(msg`Longest pages`)}
              </h2>
              <Show
                when={longestData().length > 0}
                fallback={
                  <p class="py-4 text-sm text-muted-foreground">{t(msg`No content yet.`)}</p>
                }
              >
                <BarChart
                  data={longestData()}
                  orientation="horizontal"
                  ariaLabel={t(msg`Pages ranked by number of blocks`)}
                  barClass="bg-success"
                  formatValue={fmt}
                />
              </Show>
            </section>
          </div>

          {/* Direct links to the top pages (interconnection) */}
          <Show when={stats.topLinkedPages().length > 0}>
            <section class="space-y-3">
              <h2 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground/70">
                {t(msg`Jump to a top page`)}
              </h2>
              <ul class="flex flex-wrap gap-2">
                <For each={stats.topLinkedPages()}>
                  {(entry) => (
                    <li>
                      <Link
                        to="/notes/$pageId"
                        params={{ pageId: entry.page.id }}
                        class="flex items-center gap-2 rounded-lg border border-border/40 bg-card/30 px-3 py-1.5 text-sm transition-colors hover:bg-accent/10"
                      >
                        <span class="text-base leading-none">{entry.page.icon ?? "📄"}</span>
                        <span class="max-w-[12rem] truncate">
                          {entry.page.title?.trim() || t(msg`Untitled`)}
                        </span>
                      </Link>
                    </li>
                  )}
                </For>
              </ul>
            </section>
          </Show>
        </Show>
      </div>
    </div>
  );
}
